import { forwardRef, type ReactNode, useState } from 'react';

import { cx } from 'cva';
import { useFocusable } from 'react-aria/useFocusable';
import { useObjectRef } from 'react-aria/useObjectRef';

import { Text, type TextProps } from '../text/text';
import { Tooltip, type TooltipProps } from '../tooltip/tooltip';
import { TooltipTrigger } from '../tooltip_trigger/tooltip_trigger';
import { isTruncated } from './is_truncated';

import styles from './truncated_text.module.css';

type TriggerProps = TextProps & {
  excludeFromTabOrder: boolean;
};

// `useFocusable` has to run inside the `FocusableProvider` rendered by
// `TooltipTrigger`, so the trigger cannot be inlined into `TruncatedText`.
const TruncatedTextTrigger = forwardRef<HTMLElement, TriggerProps>(
  ({ excludeFromTabOrder, ...props }, forwardedRef) => {
    const ref = useObjectRef(forwardedRef);
    const { focusableProps } = useFocusable({ excludeFromTabOrder }, ref);
    return <Text ref={ref} {...props} {...focusableProps} />;
  },
);

export type TruncatedTextProps = Omit<TextProps, 'noWrap'> & {
  maxLines?: number;
  tooltip?: ReactNode;
  tooltipPlacement?: TooltipProps['placement'];
  excludeFromTabOrder?: boolean;
};

/**
 * TruncatedText - Text that truncates in one of two ways and reveals its full
 * value in a tooltip, but only when it is actually clipped.
 *
 * Without `maxLines` the text stays on one line and is cut with an ellipsis at
 * the edge of the container.
 *
 * With `maxLines` it wraps and is clamped after that
 * many lines also with an ellipsis.
 *
 * Truncation is measured when the tooltip is about to open, so it
 * stays correct when the container or the content changes.
 *
 * > **Note:** `maxLines={1}` is not the exact same as omitting `maxLines` since they use slightly different truncation methods.
 *
 * ```tsx
 * <TruncatedText color="high_emphasis">{account.name}</TruncatedText>
 * <TruncatedText maxLines={3}>{portal.name}</TruncatedText>
 * ```
 */
export const TruncatedText = forwardRef<HTMLElement, TruncatedTextProps>(
  (
    {
      children,
      className,
      elementType = 'span',
      excludeFromTabOrder = true,
      maxLines,
      style,
      tooltip,
      tooltipPlacement,
      ...props
    },
    forwardedRef,
  ) => {
    const ref = useObjectRef(forwardedRef);
    const [isOpen, setIsOpen] = useState(false);

    const classes = cx(styles.truncated_text, className);
    const singleLineProps = { className: classes, style, noWrap: true };
    const multiLineProps = {
      className: cx(classes, styles.line_clamp),
      style: { ...style, '--max-lines': maxLines },
      noWrap: false,
    };

    return (
      <TooltipTrigger
        isOpen={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open && isTruncated(ref.current));
        }}
      >
        <TruncatedTextTrigger
          ref={ref}
          {...props}
          {...(maxLines ? multiLineProps : singleLineProps)}
          elementType={elementType}
          excludeFromTabOrder={excludeFromTabOrder}
        >
          {children}
        </TruncatedTextTrigger>
        <Tooltip placement={tooltipPlacement}>{tooltip ?? children}</Tooltip>
      </TooltipTrigger>
    );
  },
);
